import { MemberScore, WhatTeamMembersBringToThisTeam } from "./types";
import { Card, Dropdown } from "react-bootstrap";
import SectionHeader from "./SectionHeader";
import { forwardRef, useState, useEffect } from "react";
import EmptyTeamPersonalityIllustration from "resources/images/team-personality-empty.jpg";
import PersonalitySimilaritySection from "./PersonalitySimilaritySection";
import { getTagColor } from "./helper-functions";
import useCollapsibleSection from "./useCollapsibleSection";

interface Props {
  readonly teamMembers: MemberScore[];
  readonly whatTeamMembersBringToThisTeam: WhatTeamMembersBringToThisTeam;
  readonly forceExpanded?: boolean;
}

const WhatTeamMembersBringToThisTeamReport = forwardRef<HTMLDivElement, Props>(
  function WhatTeamMembersBringToThisTeamReport(
    { teamMembers, whatTeamMembersBringToThisTeam, forceExpanded },
    ref
  ) {
    const { collapsed, toggleIsCollapsed } = useCollapsibleSection(
      forceExpanded,
      true
    );
    const [selectedUserAccountId, setSelectedUserAccountId] = useState<
      number | null
    >(null);

    useEffect(() => {
      if (!selectedUserAccountId && teamMembers.length > 0) {
        setSelectedUserAccountId(teamMembers[0].userAccountId);
      }
    }, [teamMembers, selectedUserAccountId]);

    if (teamMembers.length === 0) {
      return null;
    }

    if (collapsed) {
      return (
        <Card ref={ref}>
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="What Team Members Bring To This Team"
            isCollapsed={collapsed}
          />
        </Card>
      );
    }

    if (teamMembers.length < 2) {
      return (
        <Card ref={ref}>
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="What Team Members Bring To This Team"
            isCollapsed={collapsed}
            hideActionButton={forceExpanded}
          />
          <div className="column-gap-20px align-items-center">
            <img
              src={EmptyTeamPersonalityIllustration}
              alt="empty team average illustration"
              style={{
                width: "200px",
                height: "200px",
              }}
            />
            <p>
              Once at least two of your team members have taken their
              personality assessment, their results will appear here.
            </p>
          </div>
        </Card>
      );
    }

    const getMemberContent = (teamMember: MemberScore, key: number) => {
      const bringsToThisTeam =
        whatTeamMembersBringToThisTeam[teamMember.userAccountId];
      if (!bringsToThisTeam) {
        return null;
      }

      const totalSimilarityTagClass = `${getTagColor(
        bringsToThisTeam.totalSimilarityScore
      )} label-tag m-0`;

      return (
        <div key={key}>
          <div
            style={{
              marginBottom: "10px",
              color: "#53565A",
              fontWeight: "bold",
            }}
          >
            {teamMember.firstName}'s similarity score to this team:{" "}
            <span className={totalSimilarityTagClass}>
              {Math.round(bringsToThisTeam.totalSimilarityScore)}%
            </span>
          </div>
          <div className="responsive-two-column-container">
            <PersonalitySimilaritySection
              leftStripColor="cyan"
              title="Attitudes & Outlook"
              similarityScore={bringsToThisTeam.attitudesAndOutlook}
              tagColor={getTagColor(
                bringsToThisTeam.attitudesAndOutlook.similarityScore
              )}
            />
            <PersonalitySimilaritySection
              leftStripColor="dark-blue"
              title="Work Habits"
              similarityScore={bringsToThisTeam.workHabits}
              tagColor={getTagColor(
                bringsToThisTeam.workHabits.similarityScore
              )}
            />

            <PersonalitySimilaritySection
              leftStripColor="poppy"
              title="Temperament"
              similarityScore={bringsToThisTeam.temperament}
              tagColor={getTagColor(
                bringsToThisTeam.temperament.similarityScore
              )}
            />
            <PersonalitySimilaritySection
              leftStripColor="orange"
              title="Interaction Style"
              similarityScore={bringsToThisTeam.interactionStyle}
              tagColor={getTagColor(
                bringsToThisTeam.interactionStyle.similarityScore
              )}
            />
          </div>
        </div>
      );
    };

    if (forceExpanded) {
      return (
        <Card ref={ref}>
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="What Team Members Bring To This Team"
            isCollapsed={collapsed}
            hideActionButton
          />
          {teamMembers.map((teamMember, index) => (
            <div key={teamMember.userAccountId}>
              {index !== 0 && <hr />}
              <h3>
                {teamMember.firstName} {teamMember.lastName}
              </h3>
              {getMemberContent(teamMember, index)}
            </div>
          ))}
        </Card>
      );
    }

    const selectedTeamMember = teamMembers.find(
      (teamMember) => teamMember.userAccountId === selectedUserAccountId
    );

    if (!selectedTeamMember || !selectedUserAccountId) {
      return null;
    }

    let teamMemberCount = 0;

    return (
      <Card ref={ref}>
        <SectionHeader
          toggleIsCollapsed={toggleIsCollapsed}
          title="What Team Members Bring To This Team"
          isCollapsed={collapsed}
        />
        <Dropdown>
          <Dropdown.Toggle
            variant="light"
            id="dropdown-basic"
            style={{ marginTop: "10px", marginBottom: "10px" }}
          >
            {selectedTeamMember.firstName} {selectedTeamMember.lastName}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {teamMembers.map((teamMember) => {
              teamMemberCount += 1;
              return (
                <Dropdown.Item
                  onClick={() => {
                    setSelectedUserAccountId(teamMember.userAccountId);
                  }}
                  key={teamMemberCount}
                >
                  {teamMember.firstName} {teamMember.lastName}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
        {getMemberContent(selectedTeamMember, selectedTeamMember.userAccountId)}
      </Card>
    );
  }
);

export default WhatTeamMembersBringToThisTeamReport;
